// settings/pages/OrganizationProfilePage.tsx — FIXED
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import * as api from '../services/settingsApi';
import {
    Store, ArrowLeft, Save, Building2, Phone, Mail, MapPin, Globe, Hash,
    Clock, Receipt, FileText, CheckCircle2, Sparkles, ChevronRight,
    ChevronLeft, AlertTriangle, Cloud, Download, RefreshCw,
    Cog, Eye, FileDown, Share2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================================
// PREMIUM FORM FIELD
// ============================================================
const FormField = ({ label, value, onChange, type = 'text', icon: Icon, readOnly, error, required, placeholder, helperText, disabled, className = '' }: {
    label: string; value: string; onChange?: (v: string) => void; type?: string; icon: React.ElementType;
    readOnly?: boolean; error?: string; required?: boolean; placeholder?: string; helperText?: string; disabled?: boolean; className?: string;
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const id = `field-${label.toLowerCase().replace(/\s+/g, '-')}`;

    return (
        <div className={`relative ${className}`}>
            <label htmlFor={id} className={`block text-xs font-semibold mb-1.5 transition-colors duration-200 ${isFocused ? 'text-emerald-600' : error ? 'text-rose-600' : 'text-slate-700'} ${disabled ? 'text-slate-400' : ''}`}>
                {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
            </label>
            <div className={`relative rounded-xl border-2 transition-all duration-200 overflow-hidden ${isFocused ? 'border-emerald-400 shadow-lg shadow-emerald-500/10' : error ? 'border-rose-300' : 'border-slate-200 hover:border-slate-300'} ${disabled || readOnly ? 'bg-slate-50/80' : 'bg-white '}`}>
                <div className="flex items-center">
                    {Icon && (
                        <div className={`pl-4 flex-shrink-0 transition-colors duration-200 ${isFocused ? 'text-emerald-500' : error ? 'text-rose-400' : 'text-slate-400'}`}>
                            <Icon className="w-5 h-5" />
                        </div>
                    )}
                    {type === 'textarea' ? (
                        <textarea id={id} value={value || ''} onChange={(e) => onChange?.(e.target.value)}
                            onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                            readOnly={readOnly} disabled={disabled} placeholder={placeholder} rows={3}
                            className="flex-1 px-3 py-3 text-sm bg-transparent placeholder:text-slate-400 focus:outline-none text-slate-900 resize-none" />
                    ) : (
                        <input id={id} type={type} value={value || ''} onChange={(e) => onChange?.(e.target.value)}
                            onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                            readOnly={readOnly} disabled={disabled} placeholder={placeholder}
                            className="flex-1 px-3 py-3 text-sm bg-transparent placeholder:text-slate-400 focus:outline-none text-slate-900 " />
                    )}
                </div>
            </div>
            <AnimatePresence>
                {(error || helperText) && (
                    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} className="mt-1.5 flex items-start gap-1.5 px-1">
                        {error && <AlertTriangle className="w-3.5 h-3.5 text-rose-500 mt-0.5 flex-shrink-0" />}
                        <p className={`text-xs ${error ? 'text-rose-600' : 'text-slate-500'}`}>{error || helperText}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// ============================================================
// SELECT FIELD
// ============================================================
const SelectField = ({ label, value, options, onChange, icon: Icon, disabled, placeholder, helperText, className = '' }: {
    label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void;
    icon: React.ElementType; disabled?: boolean; placeholder?: string; helperText?: string; className?: string;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find(opt => opt.value === value);
    const id = `select-${label.toLowerCase().replace(/\s+/g, '-')}`;

    // Close dropdown on outside click
    const handleBlur = (e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsOpen(false);
        }
    };

    return (
        <div className={`relative ${className}`} onBlur={handleBlur}>
            <label htmlFor={id} className={`block text-xs font-semibold mb-1.5 transition-colors duration-200 ${isOpen ? 'text-emerald-600' : 'text-slate-700'} ${disabled ? 'text-slate-400' : ''}`}>
                {label}
            </label>
            <button type="button" id={id} onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled}
                className={`relative w-full rounded-xl border-2 transition-all duration-200 ${isOpen ? 'border-emerald-400 shadow-lg shadow-emerald-500/10' : 'border-slate-200 hover:border-slate-300'} ${disabled ? 'bg-slate-50/80 cursor-not-allowed' : 'bg-white cursor-pointer'}`}>
                <div className="flex items-center">
                    {Icon && <div className={`pl-4 flex-shrink-0 ${isOpen ? 'text-emerald-500' : 'text-slate-400'}`}><Icon className="w-5 h-5" /></div>}
                    <span className={`flex-1 px-3 py-3 text-left text-sm truncate ${selectedOption ? 'text-slate-900 ' : 'text-slate-400'}`}>
                        {selectedOption?.label || placeholder || 'Select...'}
                    </span>
                    <div className="pr-4"><ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90 text-emerald-500' : 'text-slate-400'}`} /></div>
                </div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                        className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden max-h-60 overflow-y-auto">
                        {options.map((opt) => (
                            <button key={opt.value} type="button" onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                className={`w-full px-4 py-3 text-left text-sm transition-colors hover:bg-emerald-50 :bg-emerald-900/20 ${value === opt.value ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700 '}`}>
                                {opt.label}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// ============================================================
// STEP CONFIGURATION
// ============================================================
interface Step {
    num: number;
    title: string;
    icon: React.ElementType;
    description: string;
}

const STEPS: Step[] = [
    { num: 1, title: 'Company', icon: Building2, description: 'Basic business information' },
    { num: 2, title: 'Regional', icon: Globe, description: 'Currency, timezone & format' },
    { num: 3, title: 'Tax & Receipts', icon: Receipt, description: 'Tax defaults and receipt customization' },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function OrganizationProfilePage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [currentStep, setCurrentStep] = useState(1);
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [completionStatus, setCompletionStatus] = useState<Record<number, boolean>>({});

    const [form, setForm] = useState({
        name: '', address: '', phone: '', email: '', taxId: '',
        currency: 'NGN', timezone: 'Africa/Lagos', dateFormat: 'DD/MM/YYYY',
        lowStockThreshold: '10', taxRate: '7.50', taxLabel: 'VAT', receiptFooter: '',
    });

    const { isLoading } = useQuery({
        queryKey: ['tenant', user?.tenantId],
        queryFn: async () => {
            const t = await api.getTenant(user!.tenantId);
            const s = t.settings || {};
            setForm({
                name: t.name || '', address: t.address || '', phone: t.phone || '',
                email: t.email || '', taxId: t.taxId || '',
                currency: s.currency || 'NGN', timezone: s.timezone || 'Africa/Lagos',
                dateFormat: s.dateFormat || 'DD/MM/YYYY', lowStockThreshold: String(s.lowStockThreshold || 10),
                taxRate: String(s.taxRate || 7.5), taxLabel: s.taxLabel || 'VAT', receiptFooter: s.receiptFooter || '',
            });
            return t;
        },
        enabled: !!user?.tenantId,
    });

    const saveMutation = useMutation({
        mutationFn: () => api.updateTenant(user!.tenantId, {
            name: form.name, address: form.address, phone: form.phone,
            email: form.email, taxId: form.taxId,
            settings: {
                currency: form.currency, timezone: form.timezone, dateFormat: form.dateFormat,
                lowStockThreshold: parseInt(form.lowStockThreshold),
                taxRate: parseFloat(form.taxRate), taxLabel: form.taxLabel, receiptFooter: form.receiptFooter,
            },
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant'] });
            setIsDirty(false);
            setLastSaved(new Date());
            // FIXED: Use numeric key
            const stepNum = currentStep;
            setCompletionStatus(prev => {
                const updated = { ...prev };
                updated[stepNum] = true;
                return updated;
            });
            addToast({ type: 'success', title: 'Saved', message: 'Settings updated successfully.' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
        onSettled: () => setSaving(false),
    });

    const handleFieldChange = useCallback((field: string, value: string) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
    }, []);

    const handleSave = () => { setSaving(true); saveMutation.mutate(); };

    // FIXED: Helper function with proper typing
    const getCompletedCount = (): number => {
        let count = 0;
        for (let i = 1; i <= STEPS.length; i++) {
            if (completionStatus[i]) count++;
        }
        return count;
    };

    const getCompletionPercentage = (): number => {
        return Math.round((getCompletedCount() / STEPS.length) * 100);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[80vh]">
                <div className="text-center">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="w-16 h-16 rounded-2xl border-4 border-emerald-200 border-t-emerald-600 mx-auto mb-6" />
                    <p className="text-slate-500 font-medium">Loading profile...</p>
                </div>
            </div>
        );
    }

    const completedCount = getCompletedCount();
    const completionPercentage = getCompletionPercentage();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="max-w-[1440px] mx-auto p-4 lg:p-6 space-y-6">

                {/* ========== GRADIENT HERO HEADER ========== */}
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 rounded-3xl shadow-2xl">
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.3),transparent_50%)]" />
                        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-400/20 rounded-full blur-3xl" />
                    </div>
                    <div className="relative px-6 lg:px-8 py-6 lg:py-8">
                        <div className="flex items-center justify-between mb-6">
                            <button onClick={() => navigate('/settings/organization')}
                                className="flex items-center gap-2 text-emerald-100 hover:text-white transition-colors text-sm font-medium">
                                <ArrowLeft className="w-4 h-4" /> Back to Overview
                            </button>
                            <div className="flex items-center gap-3">
                                {lastSaved && (
                                    <div className="flex items-center gap-2 text-xs text-emerald-100">
                                        <Clock className="w-3.5 h-3.5" />
                                        Saved {lastSaved.toLocaleTimeString()}
                                    </div>
                                )}
                                {isDirty && (
                                    <span className="text-xs text-amber-300 bg-amber-500/20 px-3 py-1 rounded-lg font-medium flex items-center gap-1">
                                        <AlertTriangle size={12} /> Unsaved
                                    </span>
                                )}
                                <button onClick={() => navigate('/settings/organization')}
                                    className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all text-sm font-medium border border-white/20">
                                    Cancel
                                </button>
                                <button onClick={handleSave} disabled={saving || !isDirty}
                                    className="px-5 py-2 bg-white text-emerald-700 rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm shadow-lg flex items-center gap-2 disabled:opacity-50">
                                    {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</> : <><Download className="w-4 h-4" /> Save</>}
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                            <motion.div whileHover={{ scale: 1.02 }} className="relative flex-shrink-0">
                                <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-white/30 to-white/10 rounded-2xl flex items-center justify-center shadow-2xl ring-4 ring-white/20 backdrop-blur-sm">
                                    <Store className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
                                </div>
                                <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 bg-emerald-400 rounded-full ring-2 ring-white flex items-center justify-center">
                                    <Sparkles className="w-3.5 h-3.5 text-white" />
                                </div>
                            </motion.div>
                            <div className="flex-1 min-w-0">
                                <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">Edit Organization Profile</h1>
                                <p className="text-emerald-100 text-sm mt-1">Configure your business details, regional settings, and tax defaults</p>
                            </div>
                            <div className="flex gap-4 flex-shrink-0">
                                <div className="text-center px-5 py-3 bg-white/10 backdrop-blur-sm rounded-2xl">
                                    <p className="text-2xl font-bold text-white">{currentStep}/{STEPS.length}</p>
                                    <p className="text-xs text-emerald-200">Step</p>
                                </div>
                                <div className="text-center px-5 py-3 bg-white/10 backdrop-blur-sm rounded-2xl">
                                    <p className="text-2xl font-bold text-white">{completedCount}/{STEPS.length}</p>
                                    <p className="text-xs text-emerald-200">Done</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ========== MAIN CONTENT ========== */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Left Sidebar — Progress */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24">
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 ">
                                    <h3 className="font-semibold text-slate-800 text-sm">Form Progress</h3>
                                </div>
                                <div className="p-3 space-y-1">
                                    {STEPS.map((step) => {
                                        const isActive = currentStep === step.num;
                                        const isCompleted = completionStatus[step.num] || currentStep > step.num;
                                        return (
                                            <button key={step.num} onClick={() => setCurrentStep(step.num)}
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${isActive ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500' : 'hover:bg-slate-50 border-l-4 border-transparent'}`}>
                                                <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 ${isActive ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25' : isCompleted ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                                    {isCompleted && !isActive ? <CheckCircle2 className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
                                                </div>
                                                <div className="flex-1 text-left min-w-0">
                                                    <p className={`text-sm font-medium truncate ${isActive ? 'text-emerald-700' : 'text-slate-800'}`}>{step.title}</p>
                                                    <p className="text-[10px] text-slate-400 truncate">{step.description}</p>
                                                </div>
                                                {isCompleted && !isActive && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="px-5 pb-4">
                                    <div className="flex items-center justify-between text-xs mb-2">
                                        <span className="text-slate-500">Completion</span>
                                        <span className="font-semibold text-emerald-600">{completionPercentage}%</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${completionPercentage}%` }}
                                            transition={{ duration: 0.5 }}
                                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full shadow-inner"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Content — Form Steps */}
                    <div className="lg:col-span-3">
                        <AnimatePresence mode="wait">

                            {/* Step 1: Company Information */}
                            {currentStep === 1 && (
                                <motion.div key="step1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50/30 to-white flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 ring-1 ring-emerald-200">
                                                    <Building2 className="w-5 h-5 text-emerald-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-slate-800">Company Information</h3>
                                                    <p className="text-xs text-slate-500">Basic details about your business</p>
                                                </div>
                                            </div>
                                            <span className={`px-2.5 py-0.5 text-[11px] font-medium rounded-full ${completionStatus[1] ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {completionStatus[1] ? 'Complete' : 'Pending'}
                                            </span>
                                        </div>
                                        <div className="p-6 space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <FormField label="Company Name" value={form.name} onChange={(v) => handleFieldChange('name', v)} icon={Store} placeholder="Nyan POS Ltd" />
                                                <FormField label="Tax ID (TIN)" value={form.taxId} onChange={(v) => handleFieldChange('taxId', v)} icon={Hash} placeholder="12345678-0001" />
                                            </div>
                                            <FormField label="Address" value={form.address} onChange={(v) => handleFieldChange('address', v)} icon={MapPin} placeholder="123 Business Street, Lagos" type="textarea" />
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <FormField label="Phone" value={form.phone} onChange={(v) => handleFieldChange('phone', v)} icon={Phone} placeholder="+234 800 000 0000" />
                                                <FormField label="Email" value={form.email} onChange={(v) => handleFieldChange('email', v)} icon={Mail} placeholder="info@nyanpos.com" />
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 2: Regional Settings */}
                            {currentStep === 2 && (
                                <motion.div key="step2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50/30 to-white flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-blue-200">
                                                    <Globe className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-slate-800">Regional Settings</h3>
                                                    <p className="text-xs text-slate-500">Currency, timezone, and date preferences</p>
                                                </div>
                                            </div>
                                            <span className={`px-2.5 py-0.5 text-[11px] font-medium rounded-full ${completionStatus[2] ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {completionStatus[2] ? 'Complete' : 'Pending'}
                                            </span>
                                        </div>
                                        <div className="p-6 space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <SelectField label="Currency" value={form.currency} onChange={(v) => handleFieldChange('currency', v)} icon={Globe}
                                                    options={[{ value: 'NGN', label: '🇳🇬 NGN - Nigerian Naira (₦)' }, { value: 'USD', label: '🇺🇸 USD - US Dollar ($)' }, { value: 'GHS', label: '🇬🇭 GHS - Ghanaian Cedi' }, { value: 'KES', label: '🇰🇪 KES - Kenyan Shilling' }]} />
                                                <SelectField label="Timezone" value={form.timezone} onChange={(v) => handleFieldChange('timezone', v)} icon={Clock}
                                                    options={[{ value: 'Africa/Lagos', label: 'Africa/Lagos (GMT+1) 🇳🇬' }, { value: 'Africa/Nairobi', label: 'Africa/Nairobi (GMT+3) 🇰🇪' }, { value: 'Africa/Accra', label: 'Africa/Accra (GMT+0) 🇬🇭' }]} />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <FormField label="Date Format" value={form.dateFormat} onChange={(v) => handleFieldChange('dateFormat', v)} icon={Clock} placeholder="DD/MM/YYYY" />
                                                <FormField label="Low Stock Threshold" value={form.lowStockThreshold} onChange={(v) => handleFieldChange('lowStockThreshold', v)} icon={AlertTriangle} type="number" helperText="Alert when stock falls below this number" />
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 3: Tax & Receipts */}
                            {currentStep === 3 && (
                                <motion.div key="step3" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-teal-50/30 to-white flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-gradient-to-br from-teal-50 to-cyan-50 ring-1 ring-teal-200">
                                                    <Receipt className="w-5 h-5 text-teal-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-slate-800">Tax Defaults & Receipts</h3>
                                                    <p className="text-xs text-slate-500">Default tax rate and receipt customization</p>
                                                </div>
                                            </div>
                                            <span className={`px-2.5 py-0.5 text-[11px] font-medium rounded-full ${completionStatus[3] ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {completionStatus[3] ? 'Complete' : 'Pending'}
                                            </span>
                                        </div>
                                        <div className="p-6 space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <FormField label="Default Tax Rate (%)" value={form.taxRate} onChange={(v) => handleFieldChange('taxRate', v)} icon={Receipt} type="number" helperText="Applied when no specific tax is selected" />
                                                <FormField label="Tax Label" value={form.taxLabel} onChange={(v) => handleFieldChange('taxLabel', v)} icon={Hash} placeholder="VAT" helperText="Displayed on receipts (e.g., VAT, GST)" />
                                            </div>
                                            <FormField label="Receipt Footer Text" value={form.receiptFooter} onChange={(v) => handleFieldChange('receiptFooter', v)} icon={FileText} placeholder="Thank you for your patronage!" type="textarea" helperText="Appears at the bottom of all receipts" />

                                            {/* Receipt Preview */}
                                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 ">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Receipt Preview</p>
                                                <div className="bg-white rounded-lg p-4 text-center space-y-1 shadow-sm">
                                                    <p className="text-sm font-bold text-slate-900 ">{form.name || 'Your Business'}</p>
                                                    <p className="text-xs text-slate-400">────────────────────</p>
                                                    <div className="flex justify-between text-xs"><span className="text-slate-500">Subtotal</span><span className="text-slate-700 ">₦10,000.00</span></div>
                                                    <div className="flex justify-between text-xs"><span className="text-slate-500">{form.taxLabel || 'VAT'} ({form.taxRate || '0'}%)</span><span className="text-slate-700 ">₦{((10000 * parseFloat(form.taxRate || '0')) / 100).toFixed(2)}</span></div>
                                                    <p className="text-xs text-slate-400">────────────────────</p>
                                                    <div className="flex justify-between text-sm font-bold"><span className="text-slate-900 ">Total</span><span className="text-slate-900 ">₦{(10000 + (10000 * parseFloat(form.taxRate || '0')) / 100).toFixed(2)}</span></div>
                                                    {form.receiptFooter && <><p className="text-xs text-slate-400">────────────────────</p><p className="text-[10px] text-slate-400 italic">{form.receiptFooter}</p></>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Bottom Navigation */}
                        <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200 ">
                            <button onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))} disabled={currentStep === 1}
                                className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all disabled:opacity-50">
                                <ChevronLeft className="w-4 h-4" /> Previous
                            </button>
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-slate-500">Step {currentStep} of {STEPS.length}</span>
                                <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <motion.div animate={{ width: `${(currentStep / STEPS.length) * 100}%` }}
                                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full" />
                                </div>
                            </div>
                            <button onClick={() => setCurrentStep(prev => Math.min(STEPS.length, prev + 1))} disabled={currentStep === STEPS.length}
                                className="flex items-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-50">
                                Next <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}